import React from 'react';
import ReactDOM from 'react-dom';
import SnackbarPC from '@tencent/dui/lib/components/Snackbar';
import SnackbarMobile from '@tencent/dui-mobile/lib/components/Snackbar';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import i18n from '@i18next/i18n';
import LinkFloatBar from '.';
import { getRedirectUrl, openUrlWithCheck } from '@src/js/common/utils';
import { IAddonManager } from '@src/slide/application/service/addon/addon-manager.interface';
import {
    ILinkFloatBarService,
} from '@src/slide/application/service/link-manager/core/link-manager.interface';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { AddonMode } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import { ADDON, ADDON_ICON_LINK, ADDON_LINK, EDITABLE, EDITABLE_FULLSCREEN, ELEMENT_LINK, NORMAL_LINK, NO_COPY, NO_COPY_FULLSCREEN, NO_COPY_LINK, ONLY_VIEW, ONLY_VIEW_FULLSCREEN, ONLY_VIEW_LINK } from '@src/slide/application/service/link-manager/core/link-floatbar/config';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import AddonFullscreen from '@src/slide/application/service/link-manager/core/link-floatbar/addon-fullscreen';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

export class LinkFloatBarService implements ILinkFloatBarService {
    private leaveTimer: ReturnType<typeof setTimeout> | null; // 链接离开timer，多个editor公用一个
    private floatInstance: LinkFloatBar;

    public constructor(private addonManager: IAddonManager) {}

    public show = (config, noNeedRelocate) => {
        if (!this.floatInstance) {
            this.floatInstance = this.create();
        }
        this.floatInstance.show(config, noNeedRelocate);
    };

    public hide = () => {
        if (this.floatInstance) {
            this.floatInstance.hide();
        }
    };

    public isShown = () => {
        if (this.floatInstance) {
            return this.floatInstance.isShown();
        }
        return false;
    };

    public isMouseEnter = () => {
        if (this.floatInstance) {
            return !!this.floatInstance.isMouseEnterBar();
        }
        return false;
    };

    public getCurrentConfig = () =>  {
        if (this.floatInstance) {
            return this.floatInstance.state.config;
        }
        return null;
    };

    public openUrl(url: string) {
        openUrlWithCheck(getRedirectUrl(url));
    }

    public hideWithTimeout(timeout: number) {
        this.leaveTimer = setTimeout(() => { // 离开的时候不立即消失，等一会才消失
            if (!this.isMouseEnter()) {
                this.hide();
            }
        }, timeout);
    }

    public hideAndClearTimeout = () => {
        if (this.isShown()) {
            this.clearLeaveTimer();
            this.hide();
        }
    };

    public clearLeaveTimer = () => {
        if (this.leaveTimer) {
            clearTimeout(this.leaveTimer);
            this.leaveTimer = null;
        }
    };

    public showAddonFullscreen(props: any): void {
        return AddonFullscreen.showFullScreen(props);
    }

    public handleCopyUrl(href: string): void {
        if (!SlideDataServices.PrivilegeManager()?.copyable) return;
        eventBus.emit(EventKeys.TRIGGER_COPY_EVENT, (triggerCopyEvent) => {
            triggerCopyEvent(href);
            const msg: any = {
                message: i18n.t('复制链接成功'),
                type: 'success',
                autoClose: true,
            };
            if (browser.isMobile) {
                SnackbarMobile.show(msg);
            } else {
                SnackbarPC.show(msg);
            }
        });
    }

    public getDisplayPrivilege(opt: any) {
        const { addon, editState, linkData, isIframeLink, needFullscreen, needShowAddon,
            addonHandler } = opt || {};
        // 默认为普通链接
        let importConfig;
        // 判断是否为插件
        const addonDisplayMode = addon?.displayMode;
        if (browser.isMobile) {
            if (addonDisplayMode === AddonMode.IFRAME) {
                return null;
            }
            return NO_COPY_LINK;
        }
        if (addonDisplayMode === AddonMode.IFRAME) {
            importConfig = this.getIframeToolBarConfig(editState, needFullscreen, needShowAddon, addonHandler);
        } else if (isIframeLink) {
            importConfig = this.geIframeLinkToolBarConfig(editState, linkData);
        } else {
            // 为普通链接
            if (editState > PPTEditorStateType.EDITABLE) {
                // 非编辑态，不显示工具栏
                importConfig = NO_COPY_LINK;
            } else if (opt.isElemLink) {
                importConfig = ELEMENT_LINK;
            } else {
                importConfig = this.getNormalLinkConfig(NORMAL_LINK, linkData);
            }
        }
        return importConfig; // 存在dropDown配置时获取的是const的副本
    }

    public tdwOnSelectedItemCallback(mode: any, addon: any): void {
        tdw({
            opername: TDWOperationType.DOC_ADDON_PLATFORM,
            module: TDWModuleType.ADDON_IFRAME,
            action: TDWActionType.ADDON_INSERT_STYLE,
            ver5: mode === AddonMode.IFRAME ? 1 : 3,
            ver6: addon?.iframeId,
        });
    }

    public handleTdwReport(id: any, addon: any, isIframeLink?: boolean | undefined): void {
        if ((addon && addon.displayMode === AddonMode.IFRAME) || isIframeLink) {
            const { iframeId, embedUrl } = addon;
            tdw({
                opername: TDWOperationType.DOC_ADDON_PLATFORM,
                module: TDWModuleType.ADDON_INSERT_TOOLBAR,
                action: id,
                ver6: iframeId,
                ver10: new URL(embedUrl).host,
            });
        }
    }

    private create() {
        const div = document.createElement('div');
        const canvasWrapper = document.getElementById('canvas-wrapper');
        div.id = 'link-float-container-id';
        canvasWrapper?.appendChild(div);

        const floatInstance = ReactDOM.render(<LinkFloatBar
            addonManager={this.addonManager}
        />, div) as unknown as LinkFloatBar;
        return floatInstance;
    }

    private getIframeToolBarConfig(editState, needFullscreen, needShowAddon, addonHandler) {
        let importConfig;
        // 如果为卡片态插件
        if (editState === PPTEditorStateType.COPYABLE) {
            // 如果只能复制元素
            importConfig = needFullscreen ? ONLY_VIEW_FULLSCREEN : ONLY_VIEW;
        } else if (editState === PPTEditorStateType.SELECTABLE) {
            importConfig = needFullscreen ? NO_COPY_FULLSCREEN : NO_COPY;
        } else {
            importConfig = needFullscreen ? EDITABLE_FULLSCREEN : EDITABLE;
            if (needShowAddon) {
                const {
                    addonSceneItemsMap,
                    formatAddonSceneList,
                    addonScenes,
                } = addonHandler;
                (ADDON[1].config as any).items = formatAddonSceneList(addonSceneItemsMap.get(addonScenes.IFRAME), true);
                const changeModeIndex = importConfig.findIndex(item => item.id === 'changeDisplay');
                importConfig = importConfig.slice(0, changeModeIndex + 1).concat(ADDON)
                    .concat(importConfig.slice(changeModeIndex + 1));
            }
        }
        return importConfig;
    }

    private geIframeLinkToolBarConfig(editState, linkData) {
        let importConfig;
        // 如果为链接态插件，并且是process on
        if (editState === PPTEditorStateType.COPYABLE) {
            // 如果只能复制元素
            importConfig = ONLY_VIEW_LINK;
        } else if (editState === PPTEditorStateType.SELECTABLE) {
            importConfig = NO_COPY_LINK;
        } else if (linkData.iconMode) {
            importConfig = ADDON_ICON_LINK;
        } else {
            importConfig = ADDON_LINK;
        }
        return importConfig;
    }

    // 通过iconMode值，确认链接样式选项的选中状态，放进 config 里
    private getNormalLinkConfig = (normalConfig, linkData) => normalConfig.map((button) => {
        if (button.id === 'changeDisplay') {
            button.config.items?.forEach((item) => {
                item.isSelected = item.id === 'icon' ? !!linkData.iconMode : !linkData.iconMode;
            });
            return button;
        }
        return button;
    });
}
